import { useState, useEffect, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronUp, Save } from 'lucide-react';
import { isStopWord } from '../../lib/stop-words';
import { supabaseClient } from '../../lib/supabase';
import { toast } from '../../lib/toast';

interface Competitor {
  domain: string;
  url?: string;
  keywords: Array<{
    keyword: string;
    frequency: number;
    tfIdf?: number;
    density?: number;
    forms?: string[];
  }>;
}

interface KeywordComparisonTableProps {
  competitors: Competitor[];
  keywords: string[];
  additionalKeywords: Set<string>;
  keywordImportance?: Record<string, number>;
  analysisId: string;
  onSave?: () => void;
}

function getKeywordStatus(importance: number, density: number): string {
  return density > 0 ? 'Present' : 'Missing';
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'Present':
      return 'bg-green-100 text-green-800';
    case 'Missing':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
}

function cleanWordForms(forms: string[]): string[] {
  return forms
    .map(form => form
      .toLowerCase()
      // Preserve apostrophes in words like "об'єктів"
      .replace(/[.,!?;:()[\]{}«»""'']+/g, '')
      .replace(/[.,]+$/, '')
      .trim()
    )
    .filter(Boolean)
    .filter((form, index, self) => self.indexOf(form) === index);
}

function calculateKeywordImportance(
  keyword: string,
  isAdditional: boolean,
  metrics: {
    competitorCount: number;
    totalCompetitors: number;
    avgDensity: number;
    frequency: number;
    totalFrequency: number;
  }
): number {
  const frequencyScore = Math.min(4, Math.log2(metrics.frequency + 1));
  const competitorPresenceScore = Math.min(4, (metrics.competitorCount / Math.max(1, metrics.totalCompetitors)) * 5);
  const additionalBonus = isAdditional ? 2 : 0;
  const rawScore = frequencyScore + competitorPresenceScore + additionalBonus;
  return Math.min(10, Math.max(0, rawScore));
}

export function KeywordComparisonTable({ 
  competitors,
  keywords,
  additionalKeywords,
  keywordImportance = {},
  analysisId,
  onSave
}: KeywordComparisonTableProps) {
  const [page, setPage] = useState(0);
  const [itemsPerPage] = useState(50);
  const [sortField, setSortField] = useState<'competitors' | 'density'>('competitors');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showAllKeywords, setShowAllKeywords] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const keywordMetrics = useMemo(() => {
    const metrics: Record<string, {
      density: number;
      avgDensity: number;
      densityRatio: number;
      competitorCount: number;
      totalCompetitors: number;
      totalFrequency: number;
      importance: number;
      forms: string[];
      status: string;
      presencePercentage: number;
    }> = {};

    const totalCompetitors = competitors.filter(c => c.domain !== 'our').length;

    keywords.forEach(keyword => {
      const ourContent = competitors.find(c => c.domain === 'our');
      const ourKeyword = ourContent?.keywords.find(k => k.keyword === keyword);
      const forms = ourKeyword?.forms || [keyword];
      const cleanedForms = cleanWordForms(forms);
      
      const competitorData = competitors
        .filter(c => c.domain !== 'our')
        .map(comp => comp.keywords.find(k => k.keyword === keyword))
        .filter(Boolean);

      const avgDensity = competitorData.reduce((sum, k) => sum + (k?.density || 0), 0) / 
        Math.max(1, competitorData.length);

      const ourDensity = ourKeyword?.density || 0;
      const densityRatio = avgDensity > 0 ? ourDensity / avgDensity : 0;
      const totalFrequency = competitorData.reduce((sum, k) => sum + (k?.frequency || 0), 0);
      const ourFrequency = ourKeyword?.frequency || 0;
      const competitorCount = competitorData.filter(k => k && k.frequency > 0).length;

      const presencePercentage = totalCompetitors > 0 
        ? Math.round((competitorCount / totalCompetitors) * 100)
        : 0;

      const importance = keywordImportance[keyword] !== undefined 
        ? keywordImportance[keyword]
        : calculateKeywordImportance(
            keyword,
            additionalKeywords.has(keyword.toLowerCase()),
            {
              competitorCount,
              totalCompetitors,
              avgDensity,
              frequency: ourFrequency,
              totalFrequency
            }
          );

      const status = getKeywordStatus(importance, ourDensity);

      metrics[keyword] = {
        density: ourDensity,
        avgDensity,
        densityRatio,
        competitorCount,
        totalCompetitors,
        totalFrequency,
        importance,
        forms: cleanedForms,
        status,
        presencePercentage
      };
    });

    return metrics;
  }, [competitors, keywords, additionalKeywords, keywordImportance]);

  const sortedKeywords = useMemo(() => {
    return [...keywords].sort((a, b) => {
      const aIsAdditional = additionalKeywords.has(a.toLowerCase());
      const bIsAdditional = additionalKeywords.has(b.toLowerCase());
      
      if (aIsAdditional && !bIsAdditional) return -1;
      if (!aIsAdditional && bIsAdditional) return 1;

      if (sortField === 'competitors') {
        const aMetrics = keywordMetrics[a];
        const bMetrics = keywordMetrics[b];
        
        const compCountDiff = bMetrics.presencePercentage - aMetrics.presencePercentage;
        if (compCountDiff !== 0) {
          return sortDirection === 'desc' ? compCountDiff : -compCountDiff;
        }
        
        return sortDirection === 'desc' 
          ? bMetrics.totalFrequency - aMetrics.totalFrequency
          : aMetrics.totalFrequency - bMetrics.totalFrequency;
      }

      if (sortField === 'density') {
        return sortDirection === 'desc'
          ? keywordMetrics[b].densityRatio - keywordMetrics[a].densityRatio
          : keywordMetrics[a].densityRatio - keywordMetrics[b].densityRatio;
      }
      
      return 0;
    });
  }, [keywords, sortField, sortDirection, keywordMetrics, additionalKeywords]);

  const visibleKeywords = useMemo(() => {
    const start = page * itemsPerPage;
    return sortedKeywords.slice(start, start + itemsPerPage);
  }, [sortedKeywords, page, itemsPerPage]);

  const handleSort = (field: 'competitors' | 'density') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const analysisData = {
        keyword_analysis: {
          keywords: keywords.map(keyword => ({
            keyword,
            ...keywordMetrics[keyword]
          })),
          metrics: {
            avgWordCount: 0,
            maxWordCount: 0,
            avgKeywordDensity: 0
          },
          lastUpdated: new Date().toISOString()
        }
      };

      const { error } = await supabaseClient
        .from('analyses')
        .update(analysisData)
        .eq('id', analysisId);

      if (error) throw error;
      toast.success('Analysis saved successfully');
      
      // Call the onSave callback if provided
      if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error('Error saving analysis:', err);
      toast.error('Failed to save analysis');
    } finally {
      setIsSaving(false);
    }
  };

  const formatRatio = (ratio: number) => {
    if (ratio === 0) return '-';
    return ratio.toFixed(2);
  };

  const getRatioColor = (ratio: number) => {
    if (ratio === 0) return 'text-gray-400';
    if (ratio < 0.5) return 'text-red-600';
    if (ratio < 0.8) return 'text-yellow-600';
    if (ratio < 1.2) return 'text-green-600';
    return 'text-blue-600 font-bold';
  };

  const missingKeywordsCount = Object.values(keywordMetrics).filter(m => m.status === 'Missing').length;

  return (
    <div className="max-w-[1200px] mx-auto">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-base font-medium text-gray-900">Keyword Distribution Analysis</h3>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
        >
          <Save className="h-3 w-3 mr-1" />
          {isSaving ? 'Saving...' : 'Save Analysis'}
        </button>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                Keywords
              </th>
              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                Status
              </th>
              <th 
                className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20 cursor-pointer"
                onClick={() => handleSort('density')}
              >
                <div className="flex items-center">
                  <span>Density</span>
                  {sortField === 'density' && (
                    <span className="ml-1">
                      {sortDirection === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                    </span>
                  )}
                </div>
              </th>
              <th 
                className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-16 cursor-pointer"
                onClick={() => handleSort('competitors')}
              >
                <div className="flex items-center justify-center">
                  <span>%</span>
                  {sortField === 'competitors' && (
                    <span className="ml-1">
                      {sortDirection === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />}
                    </span>
                  )}
                </div>
              </th>
              {competitors.map((competitor, index) => (
                <th 
                  key={index}
                  className="w-[40px] px-1 py-2 bg-gray-50 relative"
                  style={{ height: '100px' }}
                >
                  <div 
                    className="absolute bottom-0 left-0 w-[40px] origin-bottom-left -rotate-45 transform"
                    style={{ 
                      transformOrigin: 'left bottom',
                      whiteSpace: 'nowrap',
                      width: '100px',
                      textAlign: 'left',
                      paddingLeft: '8px'
                    }}
                  >
                    {competitor.url ? (
                      <a 
                        href={competitor.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-blue-600 hover:text-blue-800"
                      >
                        {competitor.domain}
                      </a>
                    ) : (
                      <span className="text-xs font-medium text-gray-500">
                        {competitor.domain}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {visibleKeywords.map((keyword, rowIndex) => {
              const metrics = keywordMetrics[keyword];
              return (
                <tr key={rowIndex} className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'} ${
                  additionalKeywords.has(keyword.toLowerCase()) ? 'bg-blue-50' : ''
                } text-xs`}>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="flex flex-col">
                      <div className="flex items-center">
                        <span className={`rounded px-1 ${
                          additionalKeywords.has(keyword.toLowerCase()) 
                            ? 'font-medium text-blue-700'
                            : 'text-gray-900'
                        }`}>
                          {keyword}
                        </span>
                        {additionalKeywords.has(keyword.toLowerCase()) && (
                          <span className="ml-1 px-1 py-0.5 text-[10px] font-medium bg-blue-100 text-blue-800 rounded">
                            Target
                          </span>
                        )}
                      </div>
                      {metrics.forms && metrics.forms.length > 1 && (
                        <div className="text-[10px] text-gray-500 mt-0.5">
                          Forms: {metrics.forms.join(', ')}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${getStatusColor(metrics.status)}`}>
                      {metrics.status}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    <div className="flex flex-col">
                      <span className={getRatioColor(metrics.densityRatio)}>
                        {formatRatio(metrics.densityRatio)}
                      </span>
                      <span className="text-[10px] text-gray-500">
                        {metrics.density.toFixed(2)}% / {metrics.avgDensity.toFixed(2)}%
                      </span>
                    </div>
                  </td>
                  <td className="px-2 py-1.5 whitespace-nowrap text-center">
                    <span className="text-xs font-medium">
                      {metrics.presencePercentage}%
                    </span>
                  </td>
                  {competitors.map((competitor, colIndex) => {
                    const keywordData = competitor.keywords.find(k => k.keyword === keyword);
                    const frequency = keywordData ? keywordData.frequency : 0;
                    return (
                      <td 
                        key={colIndex}
                        className="px-1 py-1.5 text-center"
                      >
                        <div className="flex justify-center">
                          <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] ${
                            frequency === 0 ? 'bg-red-100 text-red-800' :
                            frequency <= 2 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-green-100 text-green-800'
                          }`}>
                            {frequency}
                          </span>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {sortedKeywords.length > itemsPerPage && (
        <div className="mt-2 flex justify-between items-center text-xs">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="px-2 py-1 text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            Previous
          </button>
          <span className="text-gray-600">
            Page {page + 1} of {Math.ceil(sortedKeywords.length / itemsPerPage)}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * itemsPerPage >= sortedKeywords.length}
            className="px-2 py-1 text-gray-600 hover:text-gray-900 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}